import type { Auftrag } from '@awf/shared';

/** Was entschieden wurde, so benannt wie der Hauptknopf, auf den geklickt wurde. */
export type EntscheidungsArt = 'freigabe' | 'ueberarbeitung';

/**
 * Was die Uebergabe-Schicht anzeigt: eine Entscheidungsart oder, solange Firestore nur den
 * fremden Anspruch zeigt, das neutrale `uebergabe` (Spec 2026-09-29-absende-rennen Abschnitt 5.3).
 */
export type SchichtArt = EntscheidungsArt | 'uebergabe';

/**
 * Das eigene Absenden, gesetzt BEIM KLICK (vor `await senden`), geleert bei einem Fehler oder
 * Laufwechsel (Spec 2026-09-29-absende-rennen Abschnitt 5.1). Es sperrt nur die Runde, zu der es gehoert;
 * alles andere sagt Firestore ueber `offeneRunde`.
 */
export type Absendung = { runId: string; runde: number; art: EntscheidungsArt };

/**
 * Die Art der zuletzt beanspruchten Entscheidung, aus Firestore abgeleitet (Spec Abschnitt 5.3): Nach einer
 * Ueberarbeitung hat `erhoeheRunde` die Runde hochgezaehlt, nach einer Freigabe nicht. Liest
 * `entschiedeneRunde` -- zulaessig, siehe Doc-Kommentar dort in packages/shared.
 */
export function entscheidungsArt(
  auftrag: Pick<Auftrag, 'runde' | 'entschiedeneRunde'>,
): EntscheidungsArt | null {
  if (auftrag.entschiedeneRunde === undefined) return null;
  return (auftrag.runde ?? 1) > auftrag.entschiedeneRunde ? 'ueberarbeitung' : 'freigabe';
}

/**
 * Ist die AKTUELLE Runde beansprucht, waehrend der Lauf noch auf die Freigabe wartet? So sieht
 * Firestore zwischen dem Anspruch der Route und `erhoeheRunde` bzw. `gibRundeFrei` aus. Liest
 * `entschiedeneRunde` -- zulaessig, siehe Doc-Kommentar dort in packages/shared.
 */
export function rundeBeansprucht(
  auftrag: Pick<Auftrag, 'status' | 'runde' | 'entschiedeneRunde'>,
): boolean {
  return (
    auftrag.status === 'wartet_auf_freigabe' && auftrag.entschiedeneRunde === (auftrag.runde ?? 1)
  );
}

/**
 * Die Art der Schicht aus Firestore, fuer eine NICHT offene Runde ohne eigenes Absenden (Codex-Runde
 * 3 zu PR 48, Entscheidung des Auftraggebers): Direkt nach dem Anspruch, vor `erhoeheRunde`, sehen
 * Freigabe und Ueberarbeitung gleich aus (`wartet_auf_freigabe`, `runde === entschiedeneRunde`) --
 * dann `uebergabe`. Sobald der Zustand unterscheidbar ist, die Art aus `entscheidungsArt`.
 */
export function schichtArtAusFirestore(
  auftrag: Pick<Auftrag, 'status' | 'runde' | 'entschiedeneRunde'>,
): SchichtArt | null {
  return rundeBeansprucht(auftrag) ? 'uebergabe' : entscheidungsArt(auftrag);
}

/**
 * Der Status, wie die Oberflaeche ihn zeigt. n8n meldet nach JEDER Entscheidung zuerst
 * `in_ueberarbeitung`, auch nach einer Freigabe, und erst danach `finalisierung`. Nach einer
 * Freigabe sprang die Schrittleiste dadurch zurueck auf "Entwurf (Ueberarbeitung)"
 * (Rueckmeldung 2026-10-13). Ob freigegeben wurde, sagt `entscheidungsArt`.
 */
export function anzeigeStatus(
  auftrag: Pick<Auftrag, 'status' | 'runde' | 'entschiedeneRunde'>,
): Auftrag['status'] {
  if (auftrag.status === 'in_ueberarbeitung' && entscheidungsArt(auftrag) === 'freigabe') {
    return 'finalisierung';
  }
  return auftrag.status;
}

/** Der Auftrag mit `anzeigeStatus` statt des gemeldeten Status, fuer reine Anzeigezwecke. */
export function zurAnzeige(auftrag: Auftrag): Auftrag {
  const status = anzeigeStatus(auftrag);
  return status === auftrag.status ? auftrag : { ...auftrag, status };
}
